import { ErrorBoundary, Shadow, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { FlatList, Skeleton } from 'native-base';
import React from 'react';

import { DirectTreasuryDetailsItemProps } from '../..';
import { EmptyDetails } from '../EmptyDetails';
import { InfoItem, InfoItemProps } from '../InfoItem';

import { parseDetailsItemData, parseExtraDetailsItemData } from './constants';

interface MoreDetailsMobileProps {
  data: InfoItemProps[];
  details: DirectTreasuryDetailsItemProps[];
  isLoading: boolean;
  isError: boolean;
  refresher: () => void;
}

const IsLoadingMoreDetailsMobile = () => (
  <VStack borderTopWidth={1} borderColor="neutral.lightest" space={Spacing['inline.xxxs']}>
    <Shadow level={1} borderRadius={BoxRadius.md}>
      <VStack
        space={Spacing['inline.nano']}
        bg="background"
        borderRadius={BoxRadius.md}
        p={Spacing['inline.xxxs']}>
        {Array.from({ length: 9 }).map((_, index) => (
          <HStack
            key={index.toString()}
            w={'full'}
            justifyContent={'space-between'}
            alignItems={'center'}>
            <Skeleton
              w="40%"
              h={Typography['fontsize.md']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
            <Skeleton
              w="40%"
              h={Typography['fontsize.md']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
          </HStack>
        ))}
      </VStack>
    </Shadow>
    <Shadow level={1} borderRadius={BoxRadius.md} mt={Spacing['inline.xxxs']}>
      <VStack space={Spacing['inline.nano']} borderRadius={BoxRadius.md} p={Spacing['inline.xxxs']}>
        {Array.from({ length: 8 }).map((_, index) => (
          <HStack
            key={index.toString()}
            w={'full'}
            justifyContent={'space-between'}
            alignItems={'center'}>
            <Skeleton
              w="40%"
              h={Typography['fontsize.md']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
            <Skeleton
              w="40%"
              h={Typography['fontsize.md']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
          </HStack>
        ))}
      </VStack>
    </Shadow>
  </VStack>
);

export const MoreDetailsMobile: React.FC<MoreDetailsMobileProps> = ({
  data,
  details,
  isLoading,
  isError,
  refresher,
}) => {
  if (!isLoading && isError) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <VStack borderTopWidth={1} borderColor="neutral.lightest" space={Spacing['inline.xxxs']}>
      <Shadow level={1} borderRadius={BoxRadius.md} mt={Spacing['inline.xxxs']}>
        <VStack
          space={Spacing['inline.nano']}
          borderRadius={BoxRadius.md}
          p={Spacing['inline.xxxs']}>
          {data.map((item) => (
            <InfoItem {...item} key={item.label} />
          ))}
        </VStack>
      </Shadow>
      {isLoading && <IsLoadingMoreDetailsMobile />}
      {!isLoading && (
        <FlatList
          data={details}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={EmptyDetails}
          keyExtractor={(item) => item.titleId}
          renderItem={({ item }) => {
            const detailsItems = parseDetailsItemData(item);
            const extraDetailsItems = parseExtraDetailsItemData(item);

            return (
              <VStack
                borderColor="neutral.lightest"
                mt={Spacing['stack.nano']}
                space={Spacing['inline.quarck']}>
                <Shadow level={1} borderRadius={BoxRadius.md}>
                  <VStack
                    space={Spacing['inline.nano']}
                    bg="background"
                    borderRadius={BoxRadius.md}
                    p={Spacing['inline.xxxs']}>
                    {detailsItems.map((detailItem) => (
                      <InfoItem {...detailItem} key={detailItem.label} />
                    ))}
                  </VStack>
                </Shadow>
                <Shadow level={1} borderRadius={BoxRadius.md} mt={Spacing['inline.xxxs']}>
                  <VStack
                    space={Spacing['inline.nano']}
                    borderRadius={BoxRadius.md}
                    p={Spacing['inline.xxxs']}>
                    {extraDetailsItems.map((extraDetailItem) => (
                      <InfoItem {...extraDetailItem} key={extraDetailItem.label} />
                    ))}
                  </VStack>
                </Shadow>
              </VStack>
            );
          }}
        />
      )}
    </VStack>
  );
};
